import {useLayoutEffect, type RefObject} from "react";
import {animate, useReducedMotion} from "motion/react";

import {LANDING_COUNT_DELAY, LANDING_COUNT_SPRING} from "./motion";
import {useScrolledPast} from "./useScrolledPast";

/**
 * Отсчёт процента и заполнение кольца при появлении на экране — один раз.
 *
 * Как устроено и почему так:
 *   • Итоговое число лежит в разметке с самого начала (`<span data-count>92</span>`),
 *     поэтому поисковик, читалка и тест видят настоящее значение, а не «0».
 *     Хук подменяет его на 0 только в браузере и только когда движение разрешено.
 *   • Пишем напрямую в DOM (`textContent`, CSS-переменная `--rl-fill`), а не в
 *     состояние React: 80 кадров счёта не должны быть 80 перерисовками дерева.
 *   • Кольцо — `conic-gradient`, и заполнить его можно только изменением стопа, то
 *     есть перерисовкой (paint), а не композитором. Это единственное место
 *     лендинга, где анимируется не transform/opacity; цена — перекрашивание круга
 *     86×86 px около 1.4 с, один раз, без влияния на layout. Эквивалент на одних
 *     transform потребовал бы двух вращающихся масок и ломал бы тот же градиент,
 *     что на утверждённом экране матча.
 *   • Пружина без отскока: число не перелетает цель (92 → 94 → 92 читалось бы
 *     как ошибка расчёта).
 *   • Если блок проскочили быстрее кадра наблюдателя, счёт не запускается и число
 *     просто остаётся итоговым (`useScrolledPast`, тот же приём, что у `Reveal`).
 *   • «Уменьшить движение» — ничего не подменяем: сразу итог.
 *
 * `ref` — элемент кольца; внутри него ищется `[data-count]`.
 */
export function useCountUp(ref: RefObject<HTMLElement | null>, target: number, delay = LANDING_COUNT_DELAY): void {
    const reduceMotion = Boolean(useReducedMotion());
    const scrolledPast = useScrolledPast(ref, !reduceMotion);

    useLayoutEffect(() => {
        const el = ref.current;
        const label = el?.querySelector<HTMLElement>("[data-count]");
        if (!el || !label || reduceMotion || scrolledPast) return;

        const write = (value: number) => {
            label.textContent = String(Math.round(value));
            el.style.setProperty("--rl-fill", String(value / 100));
        };
        const finish = () => {
            label.textContent = String(target);
            el.style.removeProperty("--rl-fill");
        };

        write(0);

        let controls: ReturnType<typeof animate> | undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                observer.disconnect();
                controls = animate(0 as number, target, {
                    ...LANDING_COUNT_SPRING,
                    delay,
                    onUpdate: write,
                    onComplete: finish,
                });
            },
            {threshold: 0.6},
        );
        observer.observe(el);

        return () => {
            observer.disconnect();
            controls?.stop();
            finish();
        };
    }, [ref, target, delay, reduceMotion, scrolledPast]);
}
